// https://boost.allnetru.pro/api/dev/dicts/routes?prefix=api/partner/mentions
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  GetMentionProfilesParams,
  GetMentionProfilesResponse,
  PostMentionInviteBody,
  PostMentionInviteResponse,
} from './models';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { MENTIONS_TAGS } from '../../tags';

const partner_mentions = MENTIONS_TAGS.PARTNER_MENTIONS;

// exported so the resolution broadcast can invalidate a stale "nobody found";
// granular rather than a bare string tag, per the RTK cache rules
export const MENTIONS_TAG = { type: partner_mentions, id: 'LIST' } as const;

export const partnerMentions = createApi({
  reducerPath: partner_mentions,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_mentions],
  endpoints: (build) => ({
    // collected profiles that can be used as mention targets on the given channel
    getMentionProfiles: build.query<GetMentionProfilesResponse, GetMentionProfilesParams>({
      query: ({ channel, query, limit }) => {
        const params = new URLSearchParams({ channel: String(channel), query });
        if (limit) {
          params.set('limit', String(limit));
        }
        return {
          url: `/partner/mentions/profiles?${params.toString()}`,
          headers: { Accept: 'application/json' },
        };
      },
      // a "nobody found" answer becomes stale the moment the invited person
      // reacts, so the tag lets the resolution broadcast invalidate it
      providesTags: [MENTIONS_TAG],
    }),

    // signed link the author sends to someone who is not in the registry yet
    createMentionInvite: build.mutation<PostMentionInviteResponse, PostMentionInviteBody>({
      query: (body) => ({
        url: '/partner/mentions/invite',
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
    }),
  }),
});

export const { useGetMentionProfilesQuery, useCreateMentionInviteMutation } = partnerMentions;
